import {ConfirmWindow} from "./ui/modules/abstract/popups/ConfirmWindow";
import type {TransportLayer} from "./data/TransportLayer";
import {XyiconView} from "./ui/modules/xyicon/XyiconView";
import {PortfolioView} from "./ui/modules/portfolio/PortfolioView";
import {SpaceView} from "./ui/modules/space/SpaceView";
import {SettingsView} from "./ui/modules/settings/SettingsView";
import {CatalogView} from "./ui/modules/catalog/CatalogView";
import type {ValidatePortfolioDetailsValidateObjectDto, ValidatePortfolioRequest} from "./generated/api/base";
import {XyiconFeature} from "./generated/api/base";
import {BoundaryView} from "./ui/modules/boundary/BoundaryView";
import {Signal} from "./utils/signal/Signal";
import {SpaceEditorView} from "./ui/modules/space/SpaceEditorView";
import {ReportView} from "./ui/modules/report/ReportView";
import {SpaceEditorViewV5} from "./ui/5.0/modules/spaceeditor/SpaceEditorViewV5";
import {PortfolioViewV5} from "./ui/5.0/modules/PortfolioViewV5";
import {SpaceViewV5} from "./ui/5.0/modules/SpaceViewV5";
import {CatalogViewV5} from "./ui/5.0/modules/CatalogViewV5";
import {XyiconViewV5} from "./ui/5.0/modules/XyiconViewV5";
import {BoundaryViewV5} from "./ui/5.0/modules/BoundaryViewV5";
import {ReportViewV5} from "./ui/5.0/modules/ReportViewV5";
import {ConfirmWindowV5} from "./ui/5.0/popup/ConfirmWindowV5";
import {SettingsViewV5} from "./ui/5.0/settings/SettingsViewV5";
import {GetPortfolioWideNavigationMenus, NavigationEnum} from "./Enums";
import {HomeDashboard} from "./ui/5.0/modules/home/HomeDashboard";
import {NoViewsPage} from "./ui/5.0/navigation/NoViewsPage";
import {ReportResultViewV5} from "./ui/5.0/modules/ReportResultViewV5";
import {DetailsViewV5} from "./ui/5.0/modules/DetailsViewV5";
import {DetailsView} from "./ui/modules/details/DetailsView";
import {BrowserWindow} from "./utils/device/BrowserWindow";
import {XHRLoader} from "./utils/loader/XHRLoader";
import {StringUtils} from "./utils/data/string/StringUtils";
import {notify} from "./utils/Notify";
import type {INotificationParamsV5} from "./ui/5.0/popup/NotificationV5";
import {NotificationType} from "./ui/5.0/popup/NotificationV5";
import {ObjectUtils} from "./utils/data/ObjectUtils";
import {TimeUtils} from "./utils/TimeUtils";
import type {Portfolio} from "./data/models/Portfolio";
import type {Space} from "./data/models/Space";
import type {AppNotifications} from "./ui/notification/AppNotifications";
import {getGeneralValidationErrorMessage, getGeneralValidationErrorTitle, processValidationResult} from "./ui/5.0/modules/ValidationUtils";

interface INavigationObj {
	organizationId?: string;
	portfolioRefId?: string;
	mainNav: NavigationEnum;
	subNavs?: string[];
	replacePreviousHashInHistory?: boolean;
}

interface IUnsavedChanges {
	title: string;
	description: string;
	onSave: () => Promise<void>;
}

const LoggingOutText = "Logging out...";
const RedirectingToSSOText = "Redirecting to SSO login...";

export const GetAuthenticationRelatedLoadingTexts = () => [LoggingOutText, RedirectingToSSOText];

export interface IMenu {
	label: string;
	nav: NavigationEnum;
	icon: string;
	component?: React.ComponentClass | React.FunctionComponent;
	componentV5?: React.FunctionComponent;
	hasSub: boolean;
	//section: "portfolio" | "modules";
	feature?: XyiconFeature;
	admin?: boolean;
	hideFromSideBar?: boolean;
}
export class Navigation {
	public unsavedChanges: IUnsavedChanges | null = null;
	private readonly _redirectAfterLoginKey = "xyicon-redirect-after-login";

	public signals = {
		navigate: new Signal(),
		replaceState: new Signal(),
	};

	public lastNonSettingsHash = "";
	public previousHash = "";
	private _transport: TransportLayer;

	private _menus: IMenu[] = [
		{
			label: "Portfolios",
			nav: NavigationEnum.PORTFOLIOS,
			icon: "portfolio",
			component: PortfolioView,
			componentV5: PortfolioViewV5,
			hasSub: false,
			//section: "modules",
			feature: XyiconFeature.Portfolio,
		},
		{
			label: "Home",
			nav: NavigationEnum.HOME,
			icon: "home",
			component: null,
			componentV5: HomeDashboard,
			hasSub: false,
		},
		{
			label: "Spaces",
			nav: NavigationEnum.SPACES,
			icon: "spaces",
			component: SpaceView,
			componentV5: SpaceViewV5,
			hasSub: true,
			//section: "portfolio",
			feature: XyiconFeature.Space,
		},
		{
			label: "Details",
			nav: NavigationEnum.DETAILS,
			icon: "details",
			component: DetailsView,
			componentV5: DetailsViewV5,
			hasSub: true,
			//section: "portfolio",
			hideFromSideBar: true,
		},
		{
			label: "Catalog",
			nav: NavigationEnum.CATALOG,
			icon: "catalog",
			component: CatalogView,
			componentV5: CatalogViewV5,
			hasSub: true,
			//section: "modules",
			feature: XyiconFeature.XyiconCatalog,
		},
		{
			label: "Xyicons",
			nav: NavigationEnum.XYICONS,
			icon: "xyicons",
			component: XyiconView,
			componentV5: XyiconViewV5,
			hasSub: true,
			//section: "portfolio",
			feature: XyiconFeature.Xyicon,
		},
		{
			label: "Boundaries",
			nav: NavigationEnum.BOUNDARIES,
			icon: "boundaries",
			component: BoundaryView,
			componentV5: BoundaryViewV5,
			hasSub: true,
			//section: "portfolio",
			feature: XyiconFeature.Boundary,
		},
		{
			label: "Reports",
			nav: NavigationEnum.REPORTS,
			icon: "reports",
			component: ReportView,
			componentV5: ReportViewV5,
			hasSub: true,
			//section: "modules",
			feature: XyiconFeature.Report,
		},
		{
			label: "ReportViews",
			nav: NavigationEnum.REPORT_VIEWS,
			icon: "reports",
			component: ReportView,
			componentV5: ReportResultViewV5,
			hasSub: true,
			feature: XyiconFeature.Report,
			hideFromSideBar: true,
		},
	];

	constructor(transportLayer: TransportLayer) {
		this._transport = transportLayer;
		this.previousHash = location.hash;
		this.lastNonSettingsHash =
			this._appState.selectedMenu !== NavigationEnum.SETTINGS &&
			StringUtils.isHashValid(this.previousHash, this._appState.currentUIVersion, this._appState.user)
				? this.previousHash
				: `#app/${this._appState.organizationIdByAuthData}/${this._appState.defaultMenu}`;
	}

	private get _appState() {
		return this._transport.appState;
	}

	private shouldStoreRedirectHash(hash: string) {
		if (!hash || hash.startsWith("auth/")) {
			return false;
		}

		return StringUtils.isHashValid(hash, this._appState.currentUIVersion, this._appState.user);
	}

	private storeRedirectHash(hash: string) {
		const cleanHash = StringUtils.cleanHash(hash);
		this._transport.services.auth.pathToRedirect = cleanHash;
		this._transport.services.localStorage.set(this._redirectAfterLoginKey, cleanHash);
	}

	private getStoredRedirectHash(): string {
		const authService = this._transport.services.auth;
		if (authService.pathToRedirect) {
			return authService.pathToRedirect;
		}

		const storedHash = this._transport.services.localStorage.get(this._redirectAfterLoginKey);
		return typeof storedHash === "string" ? storedHash : "";
	}

	private clearStoredRedirectHash() {
		this._transport.services.auth.pathToRedirect = "";
		this._transport.services.localStorage.remove(this._redirectAfterLoginKey);
	}

	private get _screens() {
		return [
			{
				nav: NavigationEnum.SPACE,
				label: "Space Viewer",
				component: SpaceEditorView,
				componentV5: SpaceEditorViewV5,
				feature: XyiconFeature.SpaceEditor,
			},
			{
				nav: NavigationEnum.SETTINGS,
				label: "Settings",
				component: SettingsView,
				componentV5: SettingsViewV5,
			},
			{
				nav: NavigationEnum.BLANK,
				label: "Blank",
				component: NoViewsPage,
				componentV5: NoViewsPage,
			},
		];
	}

	public revertURLToActiveOrg = () => {
		if (this._appState.organizationIdByAuthData !== this._appState.organizationIdByURL) {
			this.goApp({
				organizationId: this._appState.organizationIdByAuthData,
				mainNav: this._appState.defaultMenu,
				replacePreviousHashInHistory: true,
			});

			// If we only want to replace the orgId without modifying the active menu:
			// history.replaceState(null, "", location.hash.replace(this._appState.organizationIdByURL, this._appState.organizationIdByAuthData));
		}
	};

	/**
	 * @param hash app/{Org_Guid}/{Portfolio_RefID}/{Module}/{Identifier}
	 * @returns
	 */
	public async switchOrganizationAndPortfolioIfNecessary(hash: string, onOrganizationVerified?: () => void) {
		const parts = hash.split("/");
		if (parts[0] === "app") {
			try {
				const orgId = parts[1];
				const activePortfolioRefId = this._appState.activePortfolioRefId;
				const portfolioByUrlMaybe = this._appState.activePortfolio;

				if (orgId !== this._appState.organizationIdByAuthData) {
					const orgMaybe = this._appState.organizations.find((o) => o.id === orgId);
					if (orgMaybe) {
						// TODO: we don't have access to portfolioID here, because it's in another ogranization.
						// Need to find a way to get it

						const {isSSOEnabledForOrganization, isSSOEnabledForUser} = orgMaybe;
						const transport = this._transport;
						const appState = this._appState;

						if (isSSOEnabledForOrganization && isSSOEnabledForUser) {
							if (this.shouldStoreRedirectHash(hash)) {
								this.storeRedirectHash(hash);
							} else {
								this.clearStoredRedirectHash();
							}
							this._appState.fullscreenLoaderText = RedirectingToSSOText;
							const {loginConst} = transport.services.auth;
							const param = {
								organizationId: orgMaybe.id,
								clientID: loginConst.clientId,
								clientSecret: loginConst.clientSecret,
								email: appState.user.email,
								ssoCallBackURL: `${BrowserWindow.getURLPathWithoutQuery()}#auth/ssocallback`,
								isUserLoggedInUsingXyiconCredentials: appState.user.isUserLoggedInUsingXyiconCredentials.toString(),
							};
							const url = `${transport.apiUrl}/auth/ssosignin?${XHRLoader.encodeParams(param)}`;
							window.open(url, "_self");
						} else {
							if (isSSOEnabledForUser && !isSSOEnabledForOrganization) {
								// This should be there to identify the issue in case of SSO configuration from BE
								// and also as a help for FE dev and QA team
								this._transport.services.localStorage.set("sso-config-issue", "Something went wrong with SSO configuration from BE");
							}
							if (this._appState.user?.isUserLoggedInUsingXyiconCredentials) {
								// Proceed
							} else {
								this._appState.app.setState({
									isLoginNeededToProceed: true,
								});
								await Signal.waitForSignal(this._transport.services.auth.signals.authDataUpdated);
							}
							await this._transport.services.auth.switchOrganization(orgId /*, portfolioByUrlMaybe?.id*/);
						}
					} else {
						this.revertURLToActiveOrg();
						this.notifyUserAboutWrongOrgId();
					}

					return;
				}

				onOrganizationVerified?.();

				const portfolioIdAfterPendingSwitches = this._transport.services.auth.portfolioIdAfterPendingSwitches;

				if (portfolioByUrlMaybe) {
					if (portfolioByUrlMaybe.id !== portfolioIdAfterPendingSwitches) {
						await this._transport.services.auth.switchPortfolio(portfolioByUrlMaybe.id);
					} else if (!this._appState.lists[XyiconFeature.Xyicon].loaded) {
						// Portfolio-wide data is not yet loaded, so we need to refresh the lists
						await this._transport.services.feature.refreshList(XyiconFeature.Xyicon);
						await this._transport.services.feature.refreshList(XyiconFeature.PortfolioDocument);
					}
				} else if (activePortfolioRefId) {
					this.goToDefault(undefined, true);
					this.notifyUserAboutWrongPortfolioRefId(activePortfolioRefId);
				}
			} catch (e) {
				console.error(e);
			}
		}
	}

	public notifyUserAboutWrongOrgId = async () => {
		let notificationContainer: React.RefObject<AppNotifications> | null = this._appState.app.notificationContainer;

		if (!notificationContainer.current) {
			// It's probably redirecting to home dashboard right now, so we need to wait for a render to happen before the notification container is available
			await TimeUtils.waitForNextFrame();
			await TimeUtils.waitForNextFrame();
			notificationContainer = this._appState.app.notificationContainer;
		}

		notify(notificationContainer, {
			type: NotificationType.Warning,
			title: "Organization Not Available",
			description: "The URL is invalid or the Organization can't be accessed. Reach out to your administrator.",
		});
	};

	private notifyUserAboutWrongPortfolioRefId = async (portfolioRefId: string) => {
		let notificationContainer: React.RefObject<AppNotifications> | null = this._appState.app.notificationContainer;

		if (!notificationContainer.current) {
			// It's probably redirecting to home dashboard right now, so we need to wait for a render to happen before the notification container is available
			await TimeUtils.waitForNextFrame();
			await TimeUtils.waitForNextFrame();
			notificationContainer = this._appState.app.notificationContainer;
		}

		const moduleName = "Portfolio";

		const notificationParameters: INotificationParamsV5 = {
			type: NotificationType.Warning,
			title: getGeneralValidationErrorTitle(moduleName),
			description: getGeneralValidationErrorMessage(moduleName),
		};

		const params: ValidatePortfolioRequest = {
			portfolioRefID: portfolioRefId,
		};

		const {result, error} = await this._transport.requestForOrganization<ValidatePortfolioDetailsValidateObjectDto>({
			url: "portfolios/validate",
			method: XHRLoader.METHOD_POST,
			params,
		});

		const notAvailableInfo = processValidationResult(moduleName, this._appState, result, error);

		notificationParameters.title = notAvailableInfo.title;

		if (typeof notAvailableInfo.details === "string") {
			notificationParameters.description = notAvailableInfo.details;
		}

		notify(notificationContainer, notificationParameters);
	};

	public updatePortfolioRefIdInUrl(portfolioRefId: string, replacePreviousHashInHistory: boolean = false) {
		return new Promise<void>((resolve, reject) => {
			if (this._appState.activePortfolio?.refId !== portfolioRefId) {
				this._appState.app.navigation.signals.navigate.addOnce(resolve);
				const newHash = location.hash.replace(/^((?:[^/]*\/){2})([^/]*)(\/.*)$/, `$1${portfolioRefId}$3`);
				this.go(newHash, replacePreviousHashInHistory);
			} else {
				resolve();
			}
		});
	}

	public redirectAfterLogin() {
		const authService = this._transport.services.auth;

		const hash = this._appState.app.hash;
		const isAuthHash = hash === "" || hash.startsWith("auth/");

		if (!isAuthHash) {
			this.clearStoredRedirectHash();
			this.go(hash);
			return hash;
		}

		if (authService.authData) {
			const redirectHash = this.getStoredRedirectHash();
			if (redirectHash && this.shouldStoreRedirectHash(redirectHash)) {
				this.clearStoredRedirectHash();
				return this.go(redirectHash, true);
			}

			this.clearStoredRedirectHash();
			this.goToDefault(this._appState.organizationIdByAuthData, true);
		} else {
			this.goToLogin();
		}
	}

	public goApp(navObj: INavigationObj) {
		const defaultNavObj: INavigationObj = {
			mainNav: NavigationEnum.HOME,
			organizationId: this._appState.organizationIdByAuthData ?? "",
			portfolioRefId: this._appState.activePortfolio?.refId ?? "",
			subNavs: [],
			replacePreviousHashInHistory: false,
		};
		const navObjWithDefaults = {...defaultNavObj, ...ObjectUtils.deepCloneWithoutNullish(navObj)};
		let root: string = `app/${navObjWithDefaults.organizationId}`;

		if (GetPortfolioWideNavigationMenus().includes(navObjWithDefaults.mainNav)) {
			root += `/${navObjWithDefaults.portfolioRefId}`;
		}

		if (navObjWithDefaults.mainNav === NavigationEnum.SPACE) {
			const userId = this._transport.appState.user?.id;
			const orgId = navObjWithDefaults.organizationId;
			const spaceRefId = navObjWithDefaults.subNavs[0];
			const lastViewedSpaceId = this._appState.actions.getFeatureItemByRefId<Space>(spaceRefId, XyiconFeature.Space)?.id ?? spaceRefId;
			const localStorageKeyForLastViewedSpace = this._appState.getLocalStorageKeyForLastViewedSpace(userId, orgId);
			localStorage.setItem(localStorageKeyForLastViewedSpace, lastViewedSpaceId);
		}

		this.go(
			`${root}/${[navObjWithDefaults.mainNav, ...navObjWithDefaults.subNavs].filter((v) => !!v).join("/")}`,
			navObjWithDefaults.replacePreviousHashInHistory,
		);
	}

	public goToDefault(organizationId: string = this._appState.organizationIdByAuthData, replacePreviousHashInHistory: boolean = false) {
		const navigationObj: INavigationObj = {
			mainNav: this._appState.defaultMenu,
			organizationId,
			replacePreviousHashInHistory,
		};

		const authData = this._transport.services.auth.authData;
		const lastSelectedPortfolio = this._appState.actions.getFeatureItemById<Portfolio>(authData.portfolioID, XyiconFeature.Portfolio);
		if (lastSelectedPortfolio) {
			if (
				this._transport.appState.currentUIVersion === "4.0" &&
				!StringUtils.isHashValid(this._appState.app.hash, this._transport.appState.currentUIVersion, this._transport.appState.user)
			) {
				navigationObj.portfolioRefId = lastSelectedPortfolio.refId;
				navigationObj.subNavs = [lastSelectedPortfolio.refId];
			}
		} else {
			authData.portfolioID = "";
			this._transport.services.auth.saveAuthData(authData);
		}

		this.goApp(navigationObj);
	}

	public goToLogin() {
		if (!this._appState.user) {
			const currentHash = this._appState.app.hash;

			if (currentHash && !currentHash.startsWith("auth/")) {
				if (this.shouldStoreRedirectHash(currentHash)) {
					this.storeRedirectHash(currentHash);
				} else {
					this.clearStoredRedirectHash();
				}
			}
		}

		this._appState.app.transport.services.auth.cleanAuth();

		if (this._appState.app.hash !== "auth/login") {
			this._appState.fullscreenLoaderText = LoggingOutText;
			this.go("auth/login");
			location.reload();
		}
	}

	public goToSSOLogin() {
		if (!this._appState.user) {
			const currentHash = location.hash;
			const cleanCurrentHash = StringUtils.cleanHash(currentHash);

			if (cleanCurrentHash && !cleanCurrentHash.startsWith("auth/")) {
				if (this.shouldStoreRedirectHash(currentHash)) {
					this.storeRedirectHash(currentHash);
				} else {
					this.clearStoredRedirectHash();
				}
			}
		}

		this._appState.app.transport.services.auth.cleanAuth();
		this.go("auth/login/sso");
	}

	public goToSSOLoginFailed(status: boolean, message: string) {
		//TODO: After BE updates the error messages convention remove the ternary operator for the message
		this.go(`auth/sso-login-failed?isSuccess=${status}&message=${message === "SSO_EMAIL_MISMATCH" ? "email_mismatch" : message}`);
	}

	public async go(hash: string, replacePreviousHashInHistory: boolean = false) {
		// Remove leading "#"
		const cleanLocationHash = StringUtils.cleanHash(location.hash);
		const cleanHash = StringUtils.cleanHash(hash);

		if (cleanHash !== cleanLocationHash) {
			if (this.unsavedChanges) {
				const ConfirmWindowComponent = this._transport.appState.currentUIVersion === "5.0" ? ConfirmWindowV5 : ConfirmWindow;

				const result = await ConfirmWindowComponent.open(this.unsavedChanges.description, this.unsavedChanges.title, {cancel: "Reset", ok: "Save"});

				switch (result) {
					case true:
						await this.unsavedChanges.onSave();
						break;
					case false:
						break;
					case null:
						// The user escaped, or closed the confirm window with "x"
						return;
				}
			}

			if (replacePreviousHashInHistory) {
				history.replaceState(null, "", `#${cleanHash}`);
				this.signals.replaceState.dispatch();
			} else {
				location.hash = `#${cleanHash}`;
			}

			this.signals.navigate.dispatch();

			this.unsavedChanges = null;
		}

		return `#${cleanHash}`;
	}

	public openInNewTab(url: string) {
		window.open(url, "_blank");
	}

	public back() {
		window.history.back();
	}

	public getTitle(nav: string) {
		return (
			this._menus.filter((menu) => menu.nav === nav).map((menu) => menu.label)[0] ||
			this._screens.filter((screen) => screen.nav === nav).map((screen) => screen.label)[0] ||
			"Home"
		);
	}

	public getComponent(nav: NavigationEnum) {
		const isV5 = this._transport.appState.currentUIVersion === "5.0";
		const prop = isV5 ? "componentV5" : "component";

		return (
			this._menus.filter((menu) => menu.nav === nav).map((menu) => menu[prop])[0] ||
			this._screens.filter((screen) => screen.nav === nav).map((screen) => screen[prop])[0] ||
			null
		);
	}

	public getFeature(nav: NavigationEnum) {
		const menus = this._menus;
		const screens = this._screens;
		const currentMenuTitle = nav;
		const selectedMenu = menus.find((m) => m.label === currentMenuTitle) ?? screens.find((s) => s.label === currentMenuTitle);

		return selectedMenu?.feature ?? XyiconFeature.Portfolio;
	}

	public getMenuByFeature(feature: XyiconFeature): NavigationEnum {
		return this._menus.find((menu) => menu.feature === feature).nav;
	}

	public get menus() {
		return this._menus;
	}

	public get screens() {
		return this._screens;
	}
}
